import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole } from '../types/user';
import authService from '../services/authService';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  error: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
  refreshAuthState: (validateIfNeeded?: boolean) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ログアウト状態を管理するためのセッションストレージキー
const LOGOUT_FLAG = 'logout_in_progress';
// 認証状態の初期化が完了したかどうかを示すフラグ
const AUTH_INIT_COMPLETE = 'auth_init_complete';
// 最後に認証状態を更新した時刻
const LAST_AUTH_REFRESH = 'last_auth_refresh';
// 認証状態の更新間隔（ミリ秒）
const AUTH_REFRESH_INTERVAL = 300000; // 5分

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 認証状態を更新する関数
  const refreshAuthState = async (validateIfNeeded: boolean = false): Promise<boolean> => {
    try {
      // トークンを取得
      const token = authService.getToken();
      if (!token) {
        console.log('トークンが存在しないため、未認証状態');
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }

      // ユーザー情報を取得
      const storedUser = authService.getUser();
      
      // グローバルなAxiosデフォルトヘッダーを設定
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // カスタムイベントリスナーを設定（一度だけ）
      if (!window['__AUTH_TOKEN_LISTENER_SET__']) {
        window.addEventListener('auth:token-updated', (event: any) => {
          const newToken = event.detail?.token;
          if (newToken) {
            console.log('トークン更新イベントを検出 - グローバルヘッダーを更新');
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
        });
        window['__AUTH_TOKEN_LISTENER_SET__'] = true;
      }

      // 認証状態を更新
      if (storedUser) {
        setUser(storedUser);
        setIsAuthenticated(true);
        
        console.log('認証状態を更新しました:', {
          isAuthenticated: true,
          user: storedUser,
          token: token.substring(0, 10) + '...',
          hasAuthHeader: !!axios.defaults.headers.common['Authorization']
        });
        
        // 最後の更新時刻を記録
        localStorage.setItem(LAST_AUTH_REFRESH, Date.now().toString());
        
        // リダイレクトループ検出フラグをクリア
        const redirectAttempted = sessionStorage.getItem('auth_redirect_attempted');
        if (redirectAttempted === 'true') {
          // ユーザー情報とトークンが存在する場合、リダイレクト試行フラグをリセット
          console.log('リダイレクト試行フラグをリセット');
          sessionStorage.removeItem('auth_redirect_attempted');
          sessionStorage.removeItem('auth_error_count');
        }
        
        return true;
      } else {
        console.log('ユーザー情報が存在しないため、認証状態を更新できません');
        
        // トークンは存在するがユーザー情報がない場合、明示的に要求された場合のみトークン検証を実行
        if (validateIfNeeded) {
          console.log('トークン検証が明示的に要求されました');
          
          // リダイレクトループ検出
          const redirectAttempted = sessionStorage.getItem('auth_redirect_attempted');
          if (redirectAttempted === 'true') {
            console.log('リダイレクトループ検出 - トークン検証をスキップ');
            return false;
          }
          
          try {
            const isTokenValid = await authService.validateToken();
            if (!isTokenValid) {
              console.log('トークン検証に失敗したため、認証状態をクリア');
              authService.clearAuth();
              setIsAuthenticated(false);
              setUser(null);
              return false;
            }
            
            // トークンは有効だがユーザー情報がない場合は、部分的に認証状態とみなす
            setIsAuthenticated(true);
            return true;
          } catch (validationError) {
            console.error('トークン検証中にエラーが発生:', validationError);
            return false;
          }
        } else {
          console.log('トークン検証はスキップされました（明示的な要求がないため）');
          // 検証せずにfalseを返す
          return false;
        }
      }
    } catch (error) {
      console.error('認証状態の更新中にエラーが発生しました:', error);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  // 初期化時に認証状態を復元
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);

        // 既に初期化が完了している場合は、一定時間経過後のみ再チェック
        if (sessionStorage.getItem(AUTH_INIT_COMPLETE) === 'true') {
          const lastRefresh = localStorage.getItem(LAST_AUTH_REFRESH);
          const now = Date.now();
          
          if (lastRefresh && (now - parseInt(lastRefresh, 10) < AUTH_REFRESH_INTERVAL)) {
            console.log('認証状態の初期化は既に完了しており、更新間隔内です。localStorageから状態を復元します。');
            // 更新間隔内でサーバー検証をスキップする場合でも、
            // isAuthenticated/user の state は必ず localStorage の情報から復元する必要がある。
            // これを行わないとリロード直後に未ログイン扱いになり、ログイン画面へ飛ばされてしまう。
            await refreshAuthState(false);
            setLoading(false);
            return;
          }
          
          console.log('認証状態の初期化は完了していますが、更新間隔を超過しているため再確認します');
        }

        // ログアウト処理中のフラグがある場合は認証状態をクリア
        if (sessionStorage.getItem(LOGOUT_FLAG) === 'true') {
          console.log('ログアウト処理中のフラグを検出 - 認証状態をクリア');
          authService.clearAuth();
          sessionStorage.removeItem(LOGOUT_FLAG);
          setUser(null);
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        // ログイン画面でない場合のみトークン検証を実行（初期状態のため）
        const isLoginPage = window.location.pathname.includes('/login');
        const shouldValidate = !isLoginPage;
        
        console.log(`初期認証状態の更新 (ログインページ: ${isLoginPage}, 検証実行: ${shouldValidate})`);
        
        // 認証状態を更新（ログインページでない場合のみトークン検証を実行）
        await refreshAuthState(shouldValidate);
        
        // 初期化完了フラグを設定
        sessionStorage.setItem(AUTH_INIT_COMPLETE, 'true');
      } catch (error) {
        console.error('認証状態の初期化中にエラーが発生しました:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
    
    // 定期的に認証状態を更新するタイマーを設定
    const authRefreshTimer = setInterval(() => {
      const lastRefresh = localStorage.getItem(LAST_AUTH_REFRESH);
      const now = Date.now();
      
      // 最後の更新から一定時間経過している場合のみ更新
      if (!lastRefresh || (now - parseInt(lastRefresh, 10) >= AUTH_REFRESH_INTERVAL)) {
        console.log('定期的な認証状態の更新を実行');
        // 定期更新時はログイン画面でない場合のみトークン検証を実行
        const isLoginPage = window.location.pathname.includes('/login');
        refreshAuthState(!isLoginPage);
      }
    }, AUTH_REFRESH_INTERVAL);
    
    // クリーンアップ関数
    return () => {
      clearInterval(authRefreshTimer);
    };
  }, []);

  const login = async (username: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      console.log('認証を開始します...');
      const response = await authService.login(username, password);
      
      if (!response || !response.user || !response.access_token) {
        throw new Error('認証レスポンスが不正です');
      }
      
      // 認証情報を保存
      authService.setToken(response.access_token);
      authService.setUser(response.user);
      
      // 状態を更新
      setUser(response.user);
      setIsAuthenticated(true);
      
      // グローバルなAxiosデフォルトヘッダーを設定
      const token = authService.getToken(); // 念のため再取得
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('ログイン成功 - グローバルヘッダー設定:', {
          hasToken: !!token,
          hasAuthHeader: !!axios.defaults.headers.common['Authorization']
        });
      }
      
      console.log('認証が成功しました。ユーザー:', response.user);
      
      // 初期化完了フラグを設定
      sessionStorage.setItem(AUTH_INIT_COMPLETE, 'true');
      // 最後の更新時刻を記録
      localStorage.setItem(LAST_AUTH_REFRESH, Date.now().toString());
      
      // ログイン後に一度だけトークンを検証（ログイン直後なので成功するはず）
      try {
        console.log('ログイン後の検証を実行');
        // ログイン直後の検証は必ず実行する（ログイン画面フラグをクリア）
        sessionStorage.removeItem('login_in_progress');
        
        // 少し待ってからトークン検証を実行（トークン設定が確実に反映されるのを待つ）
        setTimeout(async () => {
          try {
            const isValid = await authService.validateToken();
            console.log('遅延トークン検証結果:', isValid);
          } catch (e) {
            console.warn('遅延トークン検証エラー:', e);
          }
        }, 500);
      } catch (validationError) {
        console.warn('ログイン後のトークン検証でエラーが発生しましたが、処理を続行します:', validationError);
      }
    } catch (error: any) {
      console.error('認証エラー:', error);
      
      // エラー時は認証情報をクリア
      authService.clearAuth();
      setError(error.message || 'ログインに失敗しました');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      console.log('ログアウト処理を開始します...');
      
      // ログアウト処理中のフラグを設定
      sessionStorage.setItem(LOGOUT_FLAG, 'true');
      
      // 初期化完了フラグをクリア
      sessionStorage.removeItem(AUTH_INIT_COMPLETE);
      // 最後の更新時刻をクリア
      localStorage.removeItem(LAST_AUTH_REFRESH);
      
      // 認証状態をクリア（先に状態を更新）
      setIsAuthenticated(false);
      setUser(null);
      
      // サーバーサイドでのログアウト処理
      try {
        await authService.logout();
      } catch (logoutError) {
        console.error('サーバーサイドのログアウト処理に失敗:', logoutError);
        // エラーが発生しても処理を継続
      }
      
      // 認証情報を完全にクリア
      authService.clearAuth();
      
      console.log('ログアウト処理が完了しました - ログイン画面にリダイレクト');
      
      // ログアウト完了後、ログイン画面にリダイレクト
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('ログアウト中にエラーが発生しました:', error);
      
      // エラーが発生した場合でも認証情報をクリア
      authService.clearAuth();
      setIsAuthenticated(false);
      setUser(null);
      
      // ログイン画面にリダイレクト
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
      // ログアウト処理中のフラグをクリア
      sessionStorage.removeItem(LOGOUT_FLAG);
    }
  };

  const hasRole = (roles: UserRole[]) => {
    if (!user || !user.role) return false;
    return roles.includes(user.role as UserRole);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        error,
        loading,
        login,
        logout,
        hasRole,
        refreshAuthState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
